import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { getMerchantPaymentSettings, createPaymentLinkSession } from '../../../services/useAPICaller';
import { PaymentSettings, Chain } from '../../../types/payment.types';
import { EXCHANGE_RATE_TYPES, ExchangeRateType } from '../../../constants/exchangeRates';
import { useI18n } from '../../../i18n/I18n';
import Step1Concept from './steps/Step1Concept';
import Step2Currency from './steps/Step2Currency';
import Step3Success from './steps/Step3Success';

interface NewPOSWizardProps {
    onClose: () => void;
    onSuccess: () => void;
}

export type WizardData = {
    name: string;
    concept: string;
    walletAddress: string;
    selectedChains: string[]; // Chain IDs or keys
    selectedTokens: string[]; // Format: "chainId:tokenSymbol"
    currency: string;
    exchangeRateType: ExchangeRateType;
    customExchangeRate: number;
    amount: number; // Not used in this flow based on description, but maybe useful? 
    // Wait, the user said "If selects ARS, then can select the prefered exchange rate". 
    // And "uSe can adjust the chane in increments of 0.1%". 
    // It seems this flows creates a *Payment Link* template or session that is reusable? 
    // Actually, "New Point of Sale" implies creating a new logical POS configuration.
    // The previous view listed "payment sessions". 
    // Step 1: Concept, Wallet, Toggles.
    // Step 2: Currency, Exchange Rate.
    // Step 3: Success.

    // Let's stick to the requested fields.
};

export default function NewPOSWizard({ onClose, onSuccess }: NewPOSWizardProps) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
    const [formData, setFormData] = useState<WizardData>({
        name: '',
        concept: '',
        walletAddress: '',
        selectedChains: [],
        selectedTokens: [],
        currency: 'ARS',
        exchangeRateType: EXCHANGE_RATE_TYPES.BLUE,
        customExchangeRate: 0,
        amount: 0 // Optional, maybe not needed for a reusable POS
    });
    const [createdSession, setCreatedSession] = useState<any>(null);

    const { t } = useI18n();
    const toast = useToast();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                await getMerchantPaymentSettings((data: PaymentSettings) => {
                    setPaymentSettings(data);
                    // Initialize wallet address from first active chain or valid one
                    if (data && data.chains && data.chains.length > 0) {
                        const defaultChain = data.chains.find(c => c.active) || data.chains[0];

                        // Collect all active tokens from active chains
                        const mainnetChains = data.chains.filter((c: Chain) => c.network === 'mainnet');
                        const activeTokens: string[] = [];
                        mainnetChains.forEach((chain: Chain) => {
                            if (chain.active) {
                                chain.tokens.forEach((token) => {
                                    if (token.active) {
                                        activeTokens.push(`${chain.id}:${token.symbol}`);
                                    }
                                });
                            }
                        });

                        setFormData(prev => ({
                            ...prev,
                            walletAddress: defaultChain.publicAddress,
                            selectedChains: mainnetChains.filter((c: Chain) => c.active).map((c: Chain) => c.id.toString()),
                            selectedTokens: activeTokens
                        }));
                    }
                });
            } catch (error) {
                console.error("Error fetching settings:", error);
                toast({ title: "Error loading settings", status: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
        // `toast` is memoized with [] by ToastProvider, so this stays a mount-only effect.
    }, [toast]);

    const handleNext = () => {
        setStep(prev => prev + 1);
    };

    const handleBack = () => {
        setStep(prev => prev - 1);
    };

    const handleCreate = async () => {
        try {
            setLoading(true);

            // Build session-specific chains from wizard selection,
            // overriding all addresses with the wallet address entered in the form
            const walletAddress = formData.walletAddress;
            const sessionChains = paymentSettings?.chains
                ?.filter(chain => chain.network === 'mainnet' && formData.selectedChains.includes(chain.id.toString()))
                .map(chain => ({
                    ...chain,
                    active: true,
                    publicAddress: walletAddress,
                    tokens: chain.tokens.map(token => ({
                        ...token,
                        active: formData.selectedTokens.includes(`${chain.id}:${token.symbol}`),
                        walletPublicAddress: walletAddress
                    }))
                })) ?? [];

            const payload: any = {
                name: formData.name,
                concept: formData.concept,
                recipient: formData.walletAddress,
                sessionType: 'PAYMENT_LINK',
                amount: 0,
                minAmount: 0,
                editable: true,
                currency: formData.currency,
                template: 'QR',
                chains: sessionChains,
                exchangeRateType: formData.currency === 'ARS' ? formData.exchangeRateType : null,
                exchangeRateAdjustment: formData.currency === 'ARS' ? formData.customExchangeRate : null
            };

            const result = await createPaymentLinkSession(payload);
            const paymentUrl = typeof result === 'string' ? result : result?.paymentLink || result?.redirectUrl || '';
            const sessionId = paymentUrl ? new URL(paymentUrl).searchParams.get('uid') : null;
            const now = new Date();
            setCreatedSession({
                ...payload,
                sessionId,
                paymentLink: paymentUrl,
                createdDate: [now.getFullYear(), now.getMonth() + 1, now.getDate(), now.getHours(), now.getMinutes()]
            });
            setStep(3);
        } catch (error) {
            console.error("Error creating POS:", error);
            toast({
                title: t('wizard.errorCreating'),
                description: t('wizard.errorCreatingDesc'),
                status: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading && !paymentSettings) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-xl" style={{ background: 'var(--color-overlay)' }}>
                <div className="text-txt-heading">Loading...</div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-hidden lg:p-4">
            <div className="w-full max-w-md h-[100dvh] lg:h-[85vh] lg:max-h-[800px] lg:rounded-3xl lg:shadow-2xl lg:border flex flex-col pt-6 pb-0 lg:overflow-hidden lg:min-h-0" style={{ background: 'var(--modal-bg)', borderColor: 'var(--color-surface-border)' }}>

                {/* Steps */}
                {step === 1 && (
                    <Step1Concept
                        formData={formData}
                        setFormData={setFormData}
                        paymentSettings={paymentSettings}
                        onNext={handleNext}
                        onCancel={onClose}
                    />
                )}
                {step === 2 && (
                    <Step2Currency
                        formData={formData}
                        setFormData={setFormData}
                        onNext={handleCreate} // Create on finish step 2
                        onBack={handleBack}
                    />
                )}
                {step === 3 && (
                    <Step3Success
                        session={createdSession}
                        onClose={() => {
                            onSuccess();
                            onClose();
                        }}
                    />
                )}

            </div>
        </div>
    );
}
